import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-velocity';
import { WindyLayerProps } from './types';
import { useWindDataFetch } from '../../hooks/useWindDataFetch';
import { Z } from '../../global/constants/zIndex';

/**
 * WindyLayer Component
 *
 * Displays animated wind particle visualization using leaflet-velocity.
 * Integrates with the existing temporal control system and layer management.
 *
 * @param typename - GeoServer layer name
 * @param workspace - GeoServer workspace
 * @param zIndex - Layer rendering priority (default: 450)
 * @param timeValue - Timestamp for temporal data (milliseconds as string)
 * @param velocityOptions - Configuration options for leaflet-velocity
 */
export function WindyLayer({
  typename,
  workspace,
  zIndex = Z.MAP_LAYER_WINDY,
  timeValue,
  velocityOptions = {},
}: WindyLayerProps) {
  const velocityLayerRef = useRef<L.VelocityLayer | null>(null);
  const isInitializedRef = useRef(false);
  const [isMapReady, setIsMapReady] = useState(false);

  // Get map instance
  const map = useMap();

  // Fetch and transform wind data
  const { windData, isLoading, error } = useWindDataFetch(
    typename,
    workspace,
    timeValue
  );

  // Effect: Wait for map to be ready
  useEffect(() => {
    if (!map) return;

    const checkMapReady = () => {
      const container = map.getContainer?.();
      const size = map.getSize?.();
      if (container && size && size.x > 0 && size.y > 0) {
        setIsMapReady(true);
      }
    };

    // Check if already ready
    checkMapReady();

    // Also listen for the 'load' event in case map isn't ready yet
    map.whenReady(() => {
      checkMapReady();
    });
  }, [map]);

  // Effect: Initialize velocity layer when map is ready
  useEffect(() => {
    if (!map || !isMapReady || isInitializedRef.current) return;

    // Double-check map container and size are valid
    const mapContainer = map.getContainer?.();
    const mapSize = map.getSize?.();
    if (!mapContainer || !mapSize || mapSize.x === 0 || mapSize.y === 0) return;

    // Default velocity options
    const defaultOptions: L.VelocityLayerOptions = {
      displayValues: true,
      displayOptions: {
        velocityType: 'Vento',
        position: 'bottomleft',
        emptyString: 'Sem dados de vento',
        angleConvention: 'bearingCW',
        showCardinal: true,
        speedUnit: 'k/h',
        directionString: 'Direção',
        speedString: 'Velocidade',
      },
      minVelocity: 0,
      maxVelocity: 15,
      velocityScale: 0.005,
      particleAge: 64,
      lineWidth: 2,
      particleMultiplier: 1 / 300,
      opacity: 0.97,
      data: null, // Will be set when windData is available
      ...velocityOptions,
    };

    // Create velocity layer
    try {
      const layer = L.velocityLayer(defaultOptions);
      velocityLayerRef.current = layer;
      layer.addTo(map);
      isInitializedRef.current = true;

    } catch (err) {
      console.error('[WindyLayer] Error creating velocity layer:', err);
    }

    // Cleanup on unmount
    return () => {
      const layer = velocityLayerRef.current;
      if (layer) {
        try {
          // Cast to any to access internal leaflet-velocity properties
          const internalLayer = layer as unknown as {
            _windy?: { stop?: () => void; animationLoop?: number };
            _canvas?: HTMLCanvasElement;
            _map?: L.Map | null;
            _animationFrame?: number;
            _displayTimeout?: NodeJS.Timeout;
          };

          // Stop the internal windy animation loop first
          if (internalLayer._windy?.stop) {
            internalLayer._windy.stop();
          }

          // Cancel any pending animation frames
          if (internalLayer._windy?.animationLoop) {
            cancelAnimationFrame(internalLayer._windy.animationLoop);
          }
          if (internalLayer._animationFrame) {
            cancelAnimationFrame(internalLayer._animationFrame);
          }

          // Clear any pending display timeout
          if (internalLayer._displayTimeout) {
            clearTimeout(internalLayer._displayTimeout);
          }

          // Clear the canvas context to stop any pending draws
          if (internalLayer._canvas) {
            const ctx = internalLayer._canvas.getContext('2d');
            if (ctx) ctx.clearRect(0, 0, internalLayer._canvas.width, internalLayer._canvas.height);
          }

          // Remove the layer from the map FIRST - this triggers onRemove which cleans up event handlers
          if (map && map.hasLayer(layer)) {
            map.removeLayer(layer);
          }

          // Nullify internal map reference to prevent any stray handlers from accessing the map
          internalLayer._map = null;
        } catch (err) {
          console.error('[WindyLayer] Error removing velocity layer:', err);
        }
        velocityLayerRef.current = null;
        isInitializedRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isMapReady]);

  // Effect: Update data when windData changes
  useEffect(() => {
    if (!velocityLayerRef.current || !windData || !map || !isMapReady) {
      return;
    }

    try {
      // Check if map container and layer are still valid before updating
      const mapContainer = map.getContainer?.();
      if (!mapContainer) return;

      if (velocityLayerRef.current && map.hasLayer(velocityLayerRef.current)) {
        velocityLayerRef.current.setData(windData);
      }
    } catch (err) {
      console.error('[WindyLayer] Error updating velocity data:', err);
    }
  }, [windData, map, isMapReady]);


  useEffect(() => {
    if (error) {
      console.error('[WindyLayer] Error loading wind data:', error);
    }
  }, [error]);

  // This component doesn't render any JSX - the layer is managed imperatively
  return null;
}
